import React, { useState, useRef } from "react";
import { Formik, Field, Form, FormikHelpers, FormikProps } from "formik";
import { Button, TextField, Grid, Link, Box, Typography } from "@mui/material";
import * as Yup from "yup";
import {
  CognitoIdentityProviderClient,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import SignInLayout from "../../layouts/SignInLayout";
import { useAuth } from "../../hooks/useAuth";

interface ResetFormValues {
  email: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
}

type SchemaType = {
  [key: string]: Yup.StringSchema;
};

const getCognitoErrorMessage = (error: any): string => {
  const code = error?.name || error?.__type || "";
  switch (code) {
    case "UserNotFoundException":
      return "No account found with this email address.";
    case "LimitExceededException":
      return "Too many attempts. Please wait a few minutes before trying again.";
    case "CodeMismatchException":
      return "The verification code is incorrect. Please check and try again.";
    case "ExpiredCodeException":
      return "The verification code has expired. Please request a new one.";
    case "InvalidPasswordException":
      return (
        error.message ||
        "Password does not meet requirements. It must be at least 8 characters with uppercase, lowercase, and a number."
      );
    case "InvalidParameterException":
      return error.message || "Invalid input. Please check your details.";
    case "NotAuthorizedException":
      return "You are not authorised to perform this action.";
    case "TooManyRequestsException":
      return "Too many requests. Please wait a moment and try again.";
    default:
      return "An unexpected error occurred. Please try again.";
  }
};

const PasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const formikRef = useRef<FormikProps<ResetFormValues>>(null);

  const client = new CognitoIdentityProviderClient({
    region: process.env.REACT_APP_COGNITO_REGION,
  });

  const getValidationSchema = () => {
    let schema: SchemaType = {
      email: Yup.string().email("Invalid email").required("Required"),
    };

    if (isCodeSent) {
      schema = {
        ...schema,
        code: Yup.string()
          .required("Required")
          .transform((value) => value?.replace(/\s/g, ""))
          .min(6, "Verification code must be 6 digits"),
        newPassword: Yup.string()
          .required("Required")
          .matches(
            new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$"),
            "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number."
          ),
        confirmPassword: Yup.string()
          .required("Please confirm your password")
          .oneOf([Yup.ref("newPassword")], "Passwords must match"),
      };
    }

    return Yup.object().shape(schema);
  };

  const initialValues: ResetFormValues = {
    email: "",
    code: "",
    newPassword: "",
    confirmPassword: "",
  };

  const handleRequestPasswordReset = async (email: string) => {
    try {
      const forgotPasswordCommand = new ForgotPasswordCommand({
        ClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
        Username: email.trim(),
      });
      await client.send(forgotPasswordCommand);
      setIsCodeSent(true);
      startResendCooldown();
      enqueueSnackbar(
        "Verification code sent to your email. Check your spam folder if necessary.",
        { variant: "success" }
      );
    } catch (error: any) {
      console.error("Error in sending password reset email:", error);
      enqueueSnackbar(getCognitoErrorMessage(error), { variant: "error" });
    }
  };

  const handleResendCode = async () => {
    const email = formikRef.current?.values?.email;
    if (!email) {
      enqueueSnackbar("Email address is missing.", { variant: "error" });
      return;
    }
    await handleRequestPasswordReset(email);
  };

  const startResendCooldown = () => {
    setIsResendDisabled(true);
    setResendCountdown(30);
    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (
    values: ResetFormValues,
    { setSubmitting }: FormikHelpers<ResetFormValues>
  ) => {
    if (!isCodeSent) {
      await handleRequestPasswordReset(values.email);
    } else {
      try {
        const confirmForgotPasswordCommand = new ConfirmForgotPasswordCommand({
          ClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
          Username: values.email.trim(),
          ConfirmationCode: values.code.replace(/\s/g, ""),
          Password: values.newPassword,
        });
        await client.send(confirmForgotPasswordCommand);
        enqueueSnackbar("Password has been reset successfully. Signing you in...", {
          variant: "success",
        });

        // Auto sign-in with the new password
        try {
          await signIn(values.email.trim(), values.newPassword);
        } catch (signInError) {
          console.warn("Auto sign-in failed, redirecting to sign-in page:", signInError);
          navigate("/auth/sign-in");
        }
      } catch (error: any) {
        console.error("Error in resetting password:", error);
        enqueueSnackbar(getCognitoErrorMessage(error), { variant: "error" });
      }
    }
    setSubmitting(false);
  };

  return (
    <SignInLayout>
      <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
        Password reset
      </Typography>

      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
      >
        {({ handleChange, handleBlur, values, errors, touched, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  name="email"
                  label="Email Address"
                  fullWidth
                  autoComplete="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email ? errors.email : ""}
                  disabled={isCodeSent}
                />
              </Grid>

              {isCodeSent && (
                <>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      name="code"
                      label="Verification Code"
                      fullWidth
                      autoComplete="one-time-code"
                      inputProps={{ inputMode: "numeric" }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        // Strip spaces on input (handles paste with spaces)
                        const stripped = e.target.value.replace(/\s/g, "");
                        setFieldValue("code", stripped);
                      }}
                      onBlur={handleBlur}
                      value={values.code}
                      error={touched.code && Boolean(errors.code)}
                      helperText={touched.code ? errors.code : ""}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      type="password"
                      name="newPassword"
                      label="New Password"
                      fullWidth
                      autoComplete="new-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.newPassword}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword ? errors.newPassword : ""}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      type="password"
                      name="confirmPassword"
                      label="Confirm New Password"
                      fullWidth
                      autoComplete="new-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.confirmPassword}
                      error={
                        touched.confirmPassword &&
                        Boolean(errors.confirmPassword)
                      }
                      helperText={
                        touched.confirmPassword ? errors.confirmPassword : ""
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="text"
                      size="small"
                      disabled={isResendDisabled}
                      onClick={handleResendCode}
                    >
                      {isResendDisabled
                        ? `Resend code (${resendCountdown}s)`
                        : "Resend code"}
                    </Button>
                  </Grid>
                </>
              )}

              <Grid
                container
                item
                xs={12}
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                  >
                    {isCodeSent ? "Reset Password" : "Send Verification Code"}
                  </Button>
                </Grid>
                <Grid item>
                  <Link
                    href="#"
                    variant="body2"
                    onClick={() => navigate("/auth/sign-in")}
                  >
                    Back to Sign In
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </SignInLayout>
  );
};

export default PasswordReset;
